/**
 * Blog posts — Firestore.
 *
 * Only documents with `status: "published"` are visible to the public; the
 * security rules enforce that as well as the query, so an unpublished draft
 * cannot be pulled by editing the client. Drafts are read by the CMS through
 * `getAllForAdmin()`, which only succeeds for an authenticated admin.
 */

import { collection, query, where, orderBy } from "firebase/firestore";
import { db } from "@/lib/firebase";
import {
  BLOG_POSTS as FALLBACK_POSTS,
  BLOG_CATEGORIES as FALLBACK_CATEGORIES,
  getBlogPostBySlug as fallbackBySlug,
} from "@/data/blogs";
import { cached, safe, fetchAll, toDateString } from "./_base";

const COLLECTION = "blogPosts";

export const toBlogPost = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    title: row.title || "",
    slug: row.slug || "",
    excerpt: row.excerpt || "",
    category: row.category || "",
    author: row.author || "Athena Scientific",
    date: toDateString(row.date),
    readTime: row.readTime || "",
    image: row.image || "",
    featured: row.featured === true,
    content: Array.isArray(row.content) ? row.content : [],
    status: row.status || "published",
    seo: row.seo || null,
  };
};

const loadPosts = () =>
  cached("blog:published", () =>
    safe(
      async () => {
        const rows = await fetchAll(
          query(
            collection(db, COLLECTION),
            where("status", "==", "published"),
            orderBy("date", "desc")
          )
        );
        return rows.map(toBlogPost);
      },
      FALLBACK_POSTS,
      "blog.getBlogPosts"
    )
  );

export const blogsService = {
  getBlogPosts: () => loadPosts(),

  /** Categories are derived from the live posts, so the filter never offers an empty one. */
  getBlogCategories: async () => {
    const posts = await loadPosts();
    const categories = Array.from(
      new Set(posts.map((post) => post.category).filter(Boolean))
    ).sort();
    return categories.length ? categories : FALLBACK_CATEGORIES;
  },

  getBlogPostBySlug: async (slug) => {
    if (!slug) return null;
    const posts = await loadPosts();
    const found = posts.find((post) => post.slug === slug);
    if (found) return found;
    // Deep link to a post that is not in the published set.
    return toBlogPost(fallbackBySlug(slug)) || null;
  },

  /** Prefer posts in the same category, then fall back to most recent. */
  getRelatedPosts: async (post, max = 2) => {
    if (!post) return [];
    const posts = (await loadPosts()).filter((candidate) => candidate.id !== post.id);
    const sameCategory = posts.filter((candidate) => candidate.category === post.category);
    const rest = posts.filter((candidate) => candidate.category !== post.category);
    return [...sameCategory, ...rest].slice(0, max);
  },
};

export default blogsService;
